import * as React from 'react';

import { HeadProps, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import CaseStudyDetailComponent, {
  caseStudyMarkStyles,
} from 'stile-shared/src/components/2024/common/CaseStudies/CaseStudyDetailComponent';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { CaseStudyStat } from 'stile-shared/src/utils/case-studies';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

import { AuCaseStudy } from 'data/caseStudies';

type AuCaseStudyWithImageData = AuCaseStudy & { imageData: IGatsbyImageData; video?: string };

interface ContextProps {
  caseStudy: AuCaseStudyWithImageData;
  allCaseStudies: AuCaseStudyWithImageData[];
}

export function Head({ pageContext }: HeadProps<unknown, ContextProps>) {
  const { caseStudy } = pageContext;
  if (!caseStudy) return null;

  return (
    <SEO
      slug={`/why-choose-stile/case-studies/${caseStudy.slug}`}
      description={caseStudy.description}
      title={caseStudy.title}
      opengraphTitle={caseStudy.title}
      opengraphDescription={caseStudy.description}
    />
  );
}

const CaseStudyDetailPageLayout = styled(PageLayout)`
  .main-content {
    background-color: rgba(255, 255, 255, 0) !important;
    margin: 0;

    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
    }
  }
  @media (max-width: 768px) {
    .header-wrapper {
      background-color: var(--stile-green-2) !important;
    }
  }
  /* The green hero sits flush under the nav, so Section's own 100px top
     padding has to go. Scoped to .section so it can't reach sections
     further down the page (VtlmSection's .content is not inside one). */
  .section .content {
    padding-top: 0 !important;
  }

  /* Concordia's title is the only one long enough to need the narrower
     measure the school name and description use. */
  &.cs-concordia-college .case-study-heading {
    max-width: 466px;
  }
`;

const PostcardBlock = styled.div`
  max-width: 764px;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  /* This block sits outside CaseStudyContent, so it needs the highlight
     treatment too — without it <mark> falls back to the browser's yellow. */
  ${caseStudyMarkStyles}

  .caption {
    margin-top: 12px;
    color: var(--stile-grey-6);
  }

  section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  p {
    margin: 0;
  }
`;

const VtlmSection = styled.div`
  background-color: var(--stile-grey-1);

  .content {
    padding: 100px 0;

    @media (max-width: 768px) {
      padding: 60px 0;
    }

    .grid-layout {
      .left {
        grid-column: 1 / span 6;

        .gatsby-image-wrapper {
          border-radius: 32px;
        }

        @media (max-width: 768px) {
          grid-column: 1 / span 12;

          .gatsby-image-wrapper {
            border-radius: 20px;
          }
        }
      }

      .right {
        grid-column: 8 / span 5;
        align-self: center;

        h2 {
          margin: 0 0 32px;
        }

        p {
          margin: 0 0 32px;
        }

        @media (max-width: 768px) {
          grid-column: 1 / span 12;
          grid-row: 2;
          margin-top: 30px;

          h2 {
            margin-bottom: 20px;
          }

          a {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
        }
      }
    }
  }
`;

export default function CaseStudyDetailPage({
  pageContext,
  ...props
}: PageProps<never, ContextProps>) {
  const { caseStudy, allCaseStudies } = pageContext;

  // Gatsby also routes this template as a page, where there is no context.
  if (!caseStudy) return null;

  const stats: CaseStudyStat[] = [
    { icon: 'location', label: 'Location', value: caseStudy.location },
    { icon: 'students', label: 'Students', value: caseStudy.students },
    { icon: 'calendar', label: 'Using Stile since', value: caseStudy.usingStileSince },
    { icon: 'sector', label: 'Sector', value: caseStudy.sector },
  ];

  return (
    <CaseStudyDetailPageLayout {...props} className={`cs-${caseStudy.slug}`}>
      {caseStudy && (
        <CaseStudyDetailComponent
          caseStudy={caseStudy}
          allCaseStudies={allCaseStudies}
          basePath="/why-choose-stile/case-studies"
          stats={stats}
          afterContent={
            caseStudy.hasVtlmSection ? (
              <VtlmSection>
                <div className="content">
                  <Grid>
                    <div className="left">
                      <StaticImage
                        src="../../../../../stile-shared/assets/images/resources/vic_vtlm_hero.jpg"
                        alt="A science teacher leading a Stile lesson in a Victorian classroom"
                        quality={100}
                      />
                    </div>
                    <div className="right">
                      <h2>Turning VTLM 2.0 into everyday practice</h2>
                      <p>
                        Built for explicit teaching and specialising in science, Stile is aligned
                        with every element of the Victorian Teaching and Learning Model.
                      </p>
                      <Button
                        href="/resources/victorian-curriculum/"
                        label={<span>Learn about Stile in Victoria</span>}
                      />
                    </div>
                  </Grid>
                </div>
              </VtlmSection>
            ) : undefined
          }
        >
          {caseStudy.hasTeachingTweakPostcard && caseStudy.fullCaseStudyContinued && (
            <PostcardBlock>
              <div>
                <StaticImage
                  src="../../../../../stile-shared/assets/images/case-studies/beenleigh-teaching-tweak.png"
                  alt="A Teaching Tweak reflection postcard used at Beenleigh State High School"
                />
                <div className="caption --small">
                  Ethan’s school have embedded the Teaching Tweak methodology into their own PL.
                </div>
              </div>
              <div dangerouslySetInnerHTML={{ __html: caseStudy.fullCaseStudyContinued }} />
            </PostcardBlock>
          )}
        </CaseStudyDetailComponent>
      )}
    </CaseStudyDetailPageLayout>
  );
}
